import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import {
  Grid,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { themedefault } from '@principal/design-system-tokens';
import { FieldValidation } from '@rjsf/utils';
import { atom, useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DismissableBanner } from '@backstage/core-components';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link } from 'react-router-dom';
import {
  ConfigApi,
  configApiRef,
  IdentityApi,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

let config: ConfigApi;
let identity: IdentityApi;
export const teamNameAtom = atom<string>('');

export const TeamNameExtensionSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};
const OrgEnum: { [key: string]: string } = {
  CHILE: 'principalchile-emu',
  DESIGNSYSTEM: 'principaldesignsystem-emu',
  DRUPAL: 'principaldrupal-emu',
  EBS: 'principalebs-emu',
  EBSDIGITALADVICE: 'principalebsdigitaladvice-emu',
  EDA: 'principaleda-emu',
  GLOBALASSETMANAGEMENT: 'principalglobalassetmanagement-emu',
  HONGKONG: 'principalhongkong-emu',
  IDI: 'principalidi-emu',
  IS: 'principalinformationservices-emu',
  MAINFRAME: 'principalmainframe-emu',
  MEXICO: 'principalmexico-emu',
  NQIT: 'principalnqit-emu',
  PFG: 'principalfinancialgroup-emu',
  RIS: 'principalris-emu',
  SBX: 'principalsbx-emu',
  SEASIA: 'principalseasia-emu',
  USIS: 'principalusis-emu',
};
export const TeamName = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  const [, setTeamName] = useAtom(teamNameAtom);
  const theme = useTheme();

  // Assigning these to variables defined outside of the scope of this function
  // so that they are available to the validator function below.
  // We do this because of how scaffolder field extensions work.
  config = useApi(configApiRef);
  identity = useApi(identityApiRef);

  // Set up variables for accounts-api validation call

  const [defaultBusinessUnit, defaultTeamName] = useMemo(() => {
    const [bu, ...team] = formData?.split('-') ?? [];

    return [bu ?? '', team?.join('-') ?? ''];
  }, [formData]);

  const formContext = useForm({
    defaultValues: {
      businessUnit: defaultBusinessUnit,
      teamName: defaultTeamName,
    },
  });

  const formattedTeamName = useMemo(() => {
    const { businessUnit, teamName } = formContext.getValues();

    return `${businessUnit}-${teamName}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formContext.formState]);

  // Normally we wouldn't use a useEffect just to update some state
  // but in this case we need to update the teamName state atom
  // while the value comes from our useMemo up above.
  useEffect(() => {
    setTeamName(formattedTeamName);
  }, [formattedTeamName, setTeamName]);

  const triggerOnChange = () => {
    const { businessUnit, teamName } = formContext.getValues();

    onChange(`${businessUnit}-${teamName}`);
  };

  const onBusinessUnitChange = (e: any) => {
    formContext.setValue('businessUnit', e.target?.value?.toUpperCase() ?? '');
    triggerOnChange();
  };

  const BuEnum: { [key: string]: string } = {
    PRINAM: 'principal-asset-management',
    BP: 'benefits-and-protection',
    SCBU: 'service-center-business-units',
    RIS: 'retirement-and-income-solutions',
    PI: 'principal-international',
  };

  const onTeamNameChange = (e: any) => {
    formContext.setValue('teamName', e.target?.value?.toUpperCase() ?? '');
    triggerOnChange();
  };

  return (
    <div>
      <DismissableBanner
        message={
          <div>
            <Grid container lg={12} xs={12}>
              <Grid item>
                <InfoOutlinedIcon />
              </Grid>
              <Grid item lg={11} style={{ padding: '10px 10px 10px 20px' }}>
                <Typography variant="body1">
                  This template supports building a new AD-backed GitHub team.
                  It does not support modifying existing teams nor building an
                  AD group only.{' '}
                  <Link to="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/pdp/components/#azure-ad-backed-github-team">
                    <strong>Visit the Engineering Handbook</strong>
                  </Link>{' '}
                  to learn more.
                </Typography>
              </Grid>
            </Grid>
          </div>
        }
        variant="info"
        id="teamName"
      />
      <h3>GitHub team name</h3>
      <Typography>
        A standarized team name will be created from the following values.
      </Typography>
      <Grid
        container
        spacing={3}
        alignItems="baseline"
        style={{ margin: '-20px -10px' }}
      >
        <Grid item>
          <FormControl
            margin="normal"
            required={required}
            error={rawErrors !== undefined && rawErrors.length > 0 && !formData}
          >
            <InputLabel htmlFor="L2 department">
              <strong>L2 business unit</strong>
            </InputLabel>
            <Select
              fullWidth
              style={{ width: '300px' }}
              value={defaultBusinessUnit}
              id="L2 department"
              onChange={onBusinessUnitChange}
            >
              {Object.keys(BuEnum).map((enumKey: any) => (
                <MenuItem key={enumKey} value={enumKey}>
                  {BuEnum[enumKey]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <FormControl
            margin="normal"
            required={required}
            error={rawErrors !== undefined && rawErrors.length > 0 && !formData}
          >
            <InputLabel htmlFor="teamName">
              <strong>Team name</strong>
            </InputLabel>
            <Input
              id="teamName"
              defaultValue={defaultTeamName}
              onChange={onTeamNameChange}
              fullWidth
              style={{ width: '300px' }}
            />
          </FormControl>
        </Grid>
      </Grid>

      <FormHelperText id="OwnershipDetailText">
        <Typography variant="body2">
          Characters remaining{' '}
          <Typography style={{ fontWeight: 700, display: 'inline' }}>
            {40 -
              formContext.getValues().teamName.length -
              formContext.getValues().businessUnit.length}
          </Typography>
          . Please use only letters, numbers, and dashes.
          <br />
          Use the abbreviated department name followed by the team name.{' '}
          <Typography style={{ fontWeight: 700, display: 'inline' }}>
            Example
          </Typography>
          : DEPARTMENT-PDP
          <h3
            style={{
              padding: '1rem 1.75rem',
              backgroundColor: themedefault.BaseColorBlueSoft10,
              fontWeight: 400,
              borderRadius: '4px',
              width: 'max-content',
              minWidth: '400px',
              color: theme.palette.common.black,
            }}
          >
            Resulting team names:{' '}
            <ul style={{ margin: '.5rem 0 0' }}>
              <li>
                <strong>GITHUB-{formattedTeamName}</strong>{' '}
                <span>(Azure / PDP)</span>
              </li>
              <li>
                <strong>{formattedTeamName}</strong> <span>(GitHub)</span>
              </li>
            </ul>
          </h3>
        </Typography>
      </FormHelperText>
    </div>
  );
};

export const validateTeamName = async (
  value: string,
  validation: FieldValidation,
  { formData }: any,
) => {
  const kebabCase = /^[a-zA-Z0-9\-]{1,40}$/g.test(value);

  if (kebabCase === false) {
    validation.addError(
      `Team name shouldn't exceed more than 40 characters and should consist only of letters, numbers, and dashes.`,
    );
  } else {
    const orgList = formData.githubOrg.map((org: string) => OrgEnum[org]);
    const githubTeamSlug = value.toLowerCase().replace(/[^a-zA-Z0-9-]/g, '-');
    const teamNames = [githubTeamSlug];
    if (formData.createAdminGroup === 'Yes') {
      teamNames.push(`${githubTeamSlug}-admin`);
    }

    const { token } = await identity.getCredentials();
    const backendHost = await config.getString('backend.baseUrl');
    const backendResponse = await fetch(
      `${backendHost}/api/validation/team-name`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: `{"teams": ${JSON.stringify(
          teamNames,
        )}, "orgList": ${JSON.stringify(orgList)}}`,
      },
    );

    const response = await backendResponse.json();
    if (response?.failures?.length > 0) {
      for (const failure of response.failures) {
        validation.addError(
          `${failure.team_name} already exists in ${failure.org} organization.`,
        );
      }
    }

    if (response?.errors?.length > 0) {
      validation.addError(
        'Internal error occurred for team name validation. Please try again later.',
      );
    }
  }
};
